import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { BoxSizeService } from '@nebalkon-workspace/storage-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';

const FIELD_LIMITS = {
  DIMENSION_MIN: 1,
  DIMENSION_MAX: 5000,
  LAYERS_MIN: 1,
  LAYERS_MAX: 10,
  WEIGHT_MIN: 0.1,
  WEIGHT_MAX: 1000,
  DESCRIPTION_MAX: 2000,
} as const;

@Component({
  selector: 'lib-staff-box-type-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, NotInput, CabinetButtonComponent],
  templateUrl: './staff-box-type-form.component.html',
  styleUrl: './staff-box-type-form.component.scss',
})
export class StaffBoxTypeFormComponent {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #fb = inject(FormBuilder);
  readonly #boxSizeService = inject(BoxSizeService);
  readonly #router = inject(Router);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);

  protected readonly form: FormGroup;
  protected readonly isSubmitting = signal(false);

  constructor() {
    this.form = this.#fb.group({
      side1Mm: [
        '',
        [
          Validators.required,
          Validators.min(FIELD_LIMITS.DIMENSION_MIN),
          Validators.max(FIELD_LIMITS.DIMENSION_MAX),
        ],
      ],
      side2Mm: [
        '',
        [
          Validators.required,
          Validators.min(FIELD_LIMITS.DIMENSION_MIN),
          Validators.max(FIELD_LIMITS.DIMENSION_MAX),
        ],
      ],
      heightMm: [
        '',
        [
          Validators.required,
          Validators.min(FIELD_LIMITS.DIMENSION_MIN),
          Validators.max(FIELD_LIMITS.DIMENSION_MAX),
        ],
      ],
      layers: [
        '1',
        [
          Validators.required,
          Validators.min(FIELD_LIMITS.LAYERS_MIN),
          Validators.max(FIELD_LIMITS.LAYERS_MAX),
        ],
      ],
      maxWeightKg: [
        '',
        [
          Validators.required,
          Validators.min(FIELD_LIMITS.WEIGHT_MIN),
          Validators.max(FIELD_LIMITS.WEIGHT_MAX),
        ],
      ],
      description: ['', [Validators.maxLength(FIELD_LIMITS.DESCRIPTION_MAX)]],
    });
  }

  protected getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return 'admin.boxTypes.create.errors.required';
    }
    if (errors['minlength']) {
      return 'admin.boxTypes.create.errors.minLength';
    }
    if (errors['maxlength']) {
      const max = errors['maxlength'].requiredLength;
      return this.#translate.instant(
        'admin.boxTypes.create.errors.maxLength',
        { max },
      );
    }
    if (errors['min']) {
      const min = errors['min'].min;
      return this.#translate.instant('admin.boxTypes.create.errors.min', {
        min,
      });
    }
    if (errors['max']) {
      const max = errors['max'].max;
      return this.#translate.instant('admin.boxTypes.create.errors.max', {
        max,
      });
    }

    return '';
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSubmitting.set(true);

    try {
      const formValue = this.form.value;
      const descriptionTrimmed =
        (formValue.description as string)?.trim() ?? '';

      await firstValueFrom(
        this.#boxSizeService.createBoxSize({
          side1_mm: Number(formValue.side1Mm),
          side2_mm: Number(formValue.side2Mm),
          height_mm: Number(formValue.heightMm),
          layers: Number(formValue.layers),
          max_weight_kg: Number(formValue.maxWeightKg),
          ...(descriptionTrimmed ? { description: descriptionTrimmed } : {}),
        }),
      );

      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.boxTypes.create.success')],
      });

      await this.#router.navigate([
        this.#cabinetPaths.staff,
        'box-types',
      ]);
    } catch (error) {
      console.error('Failed to create box type:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.boxTypes.create.error')],
      });
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
